// =============================================================================
// src/views/tasks/taskUtils.js - shared helpers for the Tasks tab.
// =============================================================================
import { isoDay } from "../../lib/tasksApi";

export const PRIORITIES = ["Low", "Normal", "High", "Urgent"];
export const CATEGORIES = ["Clinical", "Billing", "Admin", "Follow Up", "Prior Auth", "Referral", "Refill"];
export const WAITING_ON = ["Patient", "Pharmacy", "Payer", "Provider", "Other"];
export const RECURRENCE = [
  { value: "", label: "Does not repeat" },
  { value: "daily", label: "Every day" },
  { value: "weekdays", label: "Every weekday" },
  { value: "weekly", label: "Every week" },
  { value: "biweekly", label: "Every 2 weeks" },
  { value: "monthly", label: "Every month" },
];
export const PRIORITY_ORDER = { Urgent: 0, High: 1, Normal: 2, Low: 3 };

// Source labels: older writers used lower case ("portal", "automation").
const SOURCE_LABELS = { coverage_keeper: "Coverage Keeper", coverage_hub: "Coverage check" };
SOURCE_LABELS.medicaid_apply_now = "Apply now"; // Retro Window Rescue (Oct 2026)
export function sourceLabel(s) {
  if (!s) return null;
  const v = String(s);
  if (SOURCE_LABELS[v]) return SOURCE_LABELS[v];
  return v.charAt(0).toUpperCase() + v.slice(1);
}

export function patientName(t) {
  if (!t || !t.patient_id) return null;
  return [t.patient_first_name, t.patient_last_name].filter(Boolean).join(" ") || "Patient";
}

export function ageFromDob(dob) {
  if (!dob) return null;
  const d = new Date(dob + "T00:00:00");
  if (isNaN(d.getTime())) return null;
  const now = new Date();
  let age = now.getFullYear() - d.getFullYear();
  const m = now.getMonth() - d.getMonth();
  if (m < 0 || (m === 0 && now.getDate() < d.getDate())) age--;
  return age >= 0 && age < 130 ? age : null;
}

const fmtShort = (iso) => new Date(iso + "T12:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric" });

// "Overdue 3d", "Today", "Tomorrow", "Fri", "Oct 14".
export function dueLabel(due) {
  if (!due) return null;
  const today = isoDay(0);
  if (due < today) {
    const days = Math.round((new Date(today + "T12:00:00") - new Date(due + "T12:00:00")) / 86400000);
    return { text: "Overdue " + days + "d", tone: "danger" };
  }
  if (due === today) return { text: "Today", tone: "warn" };
  if (due === isoDay(1)) return { text: "Tomorrow", tone: "info" };
  if (due <= isoDay(6)) return { text: new Date(due + "T12:00:00").toLocaleDateString("en-US", { weekday: "short" }), tone: "muted" };
  return { text: fmtShort(due), tone: "muted" };
}

// Inbox groups for open tasks. Waiting tasks get their own group at the end.
export const GROUPS = [
  { key: "overdue", label: "Overdue" },
  { key: "today", label: "Today" },
  { key: "tomorrow", label: "Tomorrow" },
  { key: "week", label: "This week" },
  { key: "later", label: "Later" },
  { key: "none", label: "No due date" },
  { key: "waiting", label: "Waiting" },
];

export function groupOf(t, scope) {
  if (t.waiting_on && scope !== "waiting") return "waiting";
  const due = t.due_date;
  if (!due) return "none";
  const today = isoDay(0);
  if (due < today) return "overdue";
  if (due === today) return "today";
  if (due === isoDay(1)) return "tomorrow";
  if (due <= isoDay(6)) return "week";
  return "later";
}

// Group + order rows for the list. Returns [{ key, label, rows }].
export function buildGroups(rows, scope, sort) {
  const sorted = [...rows];
  if (sort === "priority") {
    sorted.sort((a, b) => (PRIORITY_ORDER[a.priority] ?? 9) - (PRIORITY_ORDER[b.priority] ?? 9)
      || String(a.due_date || "9999").localeCompare(String(b.due_date || "9999")));
  }
  if (scope === "done") {
    return [{ key: "done", label: "Completed and cancelled", rows: sorted }];
  }
  if (sort === "updated" || sort === "created" || sort === "priority") {
    const waiting = scope === "waiting" ? [] : sorted.filter((t) => t.waiting_on);
    const rest = scope === "waiting" ? sorted : sorted.filter((t) => !t.waiting_on);
    return [{ key: "all", label: sort === "priority" ? "By priority" : sort === "updated" ? "Recently updated" : "Newest", rows: rest }]
      .concat(waiting.length ? [{ key: "waiting", label: "Waiting", rows: waiting }] : []);
  }
  if (scope === "waiting") {
    const byOn = {};
    for (const t of sorted) (byOn[t.waiting_on || "Other"] = byOn[t.waiting_on || "Other"] || []).push(t);
    return Object.keys(byOn).sort().map((k) => ({ key: "w-" + k, label: "Waiting on " + k.toLowerCase(), rows: byOn[k] }));
  }
  const buckets = {};
  for (const t of sorted) (buckets[groupOf(t, scope)] = buckets[groupOf(t, scope)] || []).push(t);
  for (const k of Object.keys(buckets)) {
    // Within a day: Urgent first.
    buckets[k].sort((a, b) => String(a.due_date || "").localeCompare(String(b.due_date || ""))
      || (PRIORITY_ORDER[a.priority] ?? 9) - (PRIORITY_ORDER[b.priority] ?? 9));
  }
  return GROUPS.filter((g) => buckets[g.key] && buckets[g.key].length).map((g) => ({ ...g, rows: buckets[g.key] }));
}

export function checklistProgress(t) {
  const list = Array.isArray(t && t.checklist) ? t.checklist : [];
  if (!list.length) return null;
  return { done: list.filter((x) => x && x.done).length, total: list.length };
}

// Where the task came from, as a route in the staff app (or null).
export function sourceLink(t) {
  if (!t) return null;
  const pid = t.patient_id;
  const chart = (tab) => (pid ? "/patients/" + pid + (tab ? "/" + tab : "") : null);
  switch (t.related_entity) {
    case "appointment": return { label: "Open visit", to: chart("appts") };
    case "portal_feedback": return { label: "Open patient feedback", to: "/insights?tab=feedback" };
    case "chw_engagement": return { label: "Open CHW engagement", to: chart("chw_engagements") };
    case "pro_chart_prep_notes": return { label: "Open chart prep", to: chart("prep") };
    case "patient_proxy_guardianship":
    case "family_age_out_invite": return { label: "Open family access", to: chart("info") };
    case "patients": return { label: "Open chart", to: chart() };
    // Coverage tasks (Coverage Keeper renewals, NCTracks changes).
    case "coverage_keeper_case": return { label: "Open Coverage Keeper", to: "/eligibility?tab=keeper" + (pid ? "&patient=" + pid : "") };
    case "eligibility_lost_coverage":
    case "eligibility_plan_changed":
    case "eligibility_coverage_ending": return { label: "Open eligibility changes", to: "/eligibility?tab=changes" };
    // Retro Window Rescue: help a self-pay patient apply before visits age out.
    case "medicaid_apply_now": return { label: "Open Apply now", to: "/eligibility?tab=retro&view=apply" };
    default: break;
  }
  if (t.appointment_id) return { label: "Open visit", to: chart("appts") };
  return null;
}

// Snooze presets: returns an ISO timestamp.
export function snoozeAt(kind) {
  const d = new Date();
  if (kind === "3h") { d.setHours(d.getHours() + 3); return d.toISOString(); }
  if (kind === "tomorrow") { d.setDate(d.getDate() + 1); d.setHours(8, 0, 0, 0); return d.toISOString(); }
  if (kind === "monday") {
    const add = ((8 - d.getDay()) % 7) || 7;
    d.setDate(d.getDate() + add); d.setHours(8, 0, 0, 0); return d.toISOString();
  }
  return null;
}

export function fmtDateTime(iso) {
  if (!iso) return "";
  return new Date(iso).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
}

// Plain-language line for a history event.
export function describeEvent(e, nameOf) {
  const d = e.detail || {};
  switch (e.kind) {
    case "created": return "created the task" + (d.source ? " (" + sourceLabel(d.source) + ")" : "") + (d.recurrence_parent_id ? " as the next repeat" : "");
    case "status": return "changed status from " + d.from + " to " + d.to;
    case "assigned": {
      const to = d.to_user ? nameOf(d.to_user) : d.to_role ? d.to_role + " queue" : "nobody";
      return "assigned to " + to;
    }
    case "priority": return "changed priority to " + d.to;
    case "due": return d.to ? "set the due date to " + fmtShort(d.to) : "removed the due date";
    case "snooze": return d.until ? "snoozed until " + fmtDateTime(d.until) : "unsnoozed";
    case "waiting": return d.on ? "marked waiting on " + String(d.on).toLowerCase() : "cleared waiting";
    case "checklist": return "checklist " + d.done + " of " + d.total + " done";
    case "edited": {
      const parts = [];
      if (d.title) parts.push("title");
      if (d.description) parts.push("description");
      if (d.category) parts.push("category");
      if (d.patient) parts.push("patient");
      if (d.recurrence) parts.push("repeat");
      return "edited " + (parts.join(", ") || "the task");
    }
    default: return e.kind;
  }
}

// Text inputs should not trigger list shortcuts.
export function isTyping(target) {
  if (!target) return false;
  const tag = (target.tagName || "").toLowerCase();
  return tag === "input" || tag === "textarea" || tag === "select" || target.isContentEditable;
}
